// How the portal writes a number, a date, a month or a quarter. Every page uses these, so the same thing is
// written the same way everywhere, and ESLint stops anything else from creeping back in (no toFixed,
// toLocaleString, toLocaleDateString or Intl outside this file).
//
//   hours          12.5 h   12 h   <0.1 h          the grid keeps bare one-decimal cells: 7.5
//   a wait         20 min   5.7 h   2 d
//   percent        89%      a change: 3 pts
//   count          1,234
//   date           11 Aug 2026   with the time: 11 Aug 2026, 14:05 (24 hours)   day: 11 Aug
//   month          October 2026   Oct 2026   October   Oct
//   quarter        Q4 FY26 (the address keeps 2026-Q4)
//   nothing known  –
//   a download or a drawing needs no grouping or unit: decimal(7.25, 1)
//
// A calendar day written "2026-08-11" is read as written, so a timezone can never move it. An instant (an ISO
// string with a time, or a Date) is shown in the viewer's own timezone, which is what "when did this happen"
// means to the person looking. Words are English and fixed here, so they do not change with the browser's locale.

export const MISSING = '–';

type Num = number | null | undefined;
const finite = (n: Num): n is number => typeof n === 'number' && Number.isFinite(n);

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = MONTHS.map((m) => m.slice(0, 3));
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ─── Counts, hours, percent ──────────────────────────────────────

const whole = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });
const oneDecimal = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });
const fixedOneDecimal = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** 1234 → "1,234". */
export function count(n: Num): string {
  return finite(n) ? whole.format(n) : MISSING;
}

/** 1 entry, 1,234 entries. */
export function counted(n: number, one: string, many = `${one}s`): string {
  return `${count(n)} ${n === 1 ? one : many}`;
}

/** 12.5 → "12.5 h", 12 → "12 h", 0.02 → "<0.1 h", 1234.5 → "1,234.5 h". */
export function hours(h: Num): string {
  if (!finite(h)) return MISSING;
  if (h > 0 && h < 0.05) return '<0.1 h';
  return `${oneDecimal.format(h)} h`;
}

/** The number alone, one decimal always, for a cell whose heading carries the unit: 7.5, 4.0. */
export function hoursBare(h: Num): string {
  return finite(h) ? fixedOneDecimal.format(h) : MISSING;
}

/** A wait: 0.4 → "24 min", 5.7 → "5.7 h", 118 → "4.9 d", 3000 → "4.2 mo". */
export function duration(h: Num): string {
  if (!finite(h)) return MISSING;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 48) return `${oneDecimal.format(h)} h`;
  const days = h / 24;
  if (days < 60) return `${oneDecimal.format(days)} d`;
  return `${oneDecimal.format(days / 30)} mo`;
}

/** A plain number with a fixed number of decimals and no grouping, for what is not read as prose: a download, a drawing, a form value. 7.25, 1 → "7.3". */
export function decimal(n: Num, digits = 1): string {
  return finite(n) ? n.toFixed(digits) : '';
}

/** 88.6 → "89%". */
export function percent(p: Num): string {
  return finite(p) ? `${Math.round(p)}%` : MISSING;
}

/** How far a percentage moved, in points: 3 → "3 pts", 1 → "1 pt", under half a point → "Steady"; null when there is nothing to compare with. */
export function points(change: Num): string | null {
  if (!finite(change)) return null;
  const n = Math.round(Math.abs(change));
  if (n < 1) return 'Steady';
  return `${n} ${n === 1 ? 'pt' : 'pts'}`;
}

/** The same in words for a screen reader: "up 3 points", "down 1 point", "steady". */
export function pointsInWords(change: Num): string | null {
  if (!finite(change)) return null;
  const n = Math.round(Math.abs(change));
  if (n < 1) return 'steady';
  return `${change > 0 ? 'up' : 'down'} ${n} ${n === 1 ? 'point' : 'points'}`;
}

// ─── Dates ───────────────────────────────────────────────────────

interface Parts {
  y: number;
  m: number; // 1 to 12
  d: number;
  weekday: number;
  /** Present for an instant (shown in the viewer's timezone), absent for a calendar day. */
  hh?: number;
  mm?: number;
}

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

function parse(value: string | Date | null | undefined): Parts | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const day = CALENDAR_DAY.exec(value.trim());
    if (day) {
      const [y, m, d] = [Number(day[1]), Number(day[2]), Number(day[3])];
      return { y, m, d, weekday: new Date(Date.UTC(y, m - 1, d)).getUTCDay() };
    }
  }
  const at = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(at.getTime())) return null;
  return { y: at.getFullYear(), m: at.getMonth() + 1, d: at.getDate(), weekday: at.getDay(), hh: at.getHours(), mm: at.getMinutes() };
}

const two = (n: number) => String(n).padStart(2, '0');

// What shows when the date cannot be read: nothing known for no value, the text itself for text that is not a date.
const unread = (value: string | Date | null | undefined): string => (value === null || value === undefined || value === '' || typeof value !== 'string' ? MISSING : value);

/** "2026-08-11" or an instant → "11 Aug 2026". */
export function date(value: string | Date | null | undefined): string {
  const p = parse(value);
  return p ? `${p.d} ${MONTHS_SHORT[p.m - 1]} ${p.y}` : unread(value);
}

/** An instant → "11 Aug 2026, 14:05" in 24 hours; a calendar day has no time, so it stays "11 Aug 2026". */
export function dateTime(value: string | Date | null | undefined): string {
  const p = parse(value);
  if (!p) return unread(value);
  const day = `${p.d} ${MONTHS_SHORT[p.m - 1]} ${p.y}`;
  return p.hh === undefined ? day : `${day}, ${two(p.hh)}:${two(p.mm ?? 0)}`;
}

/** "2026-08-11" or an instant → "11 Aug". */
export function dayMonth(value: string | Date | null | undefined): string {
  const p = parse(value);
  return p ? `${p.d} ${MONTHS_SHORT[p.m - 1]}` : unread(value);
}

/** "2026-08-11" or an instant → "Tue 11 Aug". */
export function weekdayDay(value: string | Date | null | undefined): string {
  const p = parse(value);
  return p ? `${WEEKDAYS_SHORT[p.weekday]} ${p.d} ${MONTHS_SHORT[p.m - 1]}` : unread(value);
}

/** An instant → "14:05" (24 hours, the viewer's timezone). */
export function clock(value: string | Date | null | undefined): string {
  const p = parse(value);
  return p && p.hh !== undefined ? `${two(p.hh)}:${two(p.mm ?? 0)}` : MISSING;
}

// ─── Months, years, quarters ─────────────────────────────────────

function period(value: string): { y: number; m: number } | null {
  const hit = /^(\d{4})-(\d{2})(?:-\d{2})?$/.exec(value);
  const m = hit ? Number(hit[2]) : NaN;
  return hit && m >= 1 && m <= 12 ? { y: Number(hit[1]), m } : null;
}

/** "2026-10" → "October 2026". */
export function month(value: string): string {
  const p = period(value);
  return p ? `${MONTHS[p.m - 1]} ${p.y}` : value;
}

/** "2026-10" → "Oct 2026". */
export function monthShort(value: string): string {
  const p = period(value);
  return p ? `${MONTHS_SHORT[p.m - 1]} ${p.y}` : value;
}

/** "2026-10" → "October". */
export function monthOnly(value: string): string {
  const p = period(value);
  return p ? MONTHS[p.m - 1] : value;
}

/** "2026-10" → "Oct". */
export function monthAbbr(value: string): string {
  const p = period(value);
  return p ? MONTHS_SHORT[p.m - 1] : value;
}

/** 2026 → "2026". */
export function year(y: number | string): string {
  return String(y);
}

/** "2026-Q4" or "2026-4" (what the pickers hold) → "Q4 FY26". The address keeps 2026-Q4. */
export function quarter(value: string): string {
  const hit = /^(\d{4})-Q?([1-4])$/i.exec(value.trim());
  return hit ? `Q${hit[2]} FY${hit[1].slice(2)}` : value;
}
